import { useState, useRef } from 'react';
import { uploadBlob } from '../../services/blobUpload';
import { LoadingSpinner } from './LoadingSpinner';
import { ErrorBanner } from './ErrorBanner';

interface PhotoUploadProps {
  onUploaded: (blobUrl: string) => void;
  currentUrl?: string | null;
}

export function PhotoUpload({ onUploaded, currentUrl }: PhotoUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(currentUrl ?? null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    setError(null);
    setUploading(true);
    try {
      const objectUrl = URL.createObjectURL(file);
      setPreview(objectUrl);
      const blobUrl = await uploadBlob(file);
      onUploaded(blobUrl);
    } catch (err) {
      setError((err as Error).message);
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div>
      <div
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => inputRef.current?.click()}
        style={{
          border: '2px dashed #90caf9',
          borderRadius: 8,
          padding: 16,
          textAlign: 'center',
          cursor: 'pointer',
          background: '#f3f8ff',
          minHeight: 80,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {uploading ? (
          <LoadingSpinner size={24} />
        ) : preview ? (
          <img src={preview} alt="Preview" style={{ maxHeight: 120, maxWidth: '100%', borderRadius: 6 }} />
        ) : (
          <span style={{ color: '#64b5f6', fontSize: 13 }}>Click or drag a photo to upload</span>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,.heic,.heif"
        style={{ display: 'none' }}
        onChange={handleChange}
      />
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
    </div>
  );
}
